import { type ColumnDef } from '@/lib/tanstack-table';
import { T } from 'gt-react';
import {
  RobotIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  NotebookIcon,
  UserIcon,
} from '@phosphor-icons/react';
import {
  QuoteSheetLineItemAvailability,
  QuoteSheetLineItemDTO,
} from '@/api/procurement/quote-sheets-types';
import { useDataTable } from '@/components/common/data-table/use-data-table';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { RobotRetryButton } from '@/components/common/robot-retry-button';
import { DataTableRowActions } from '@/components/common/data-table/data-table-row-actions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteQuoteSheetLineItem } from '@/api/procurement/useDeleteQuoteSheetLineItem';
import { cn } from '@/lib/utils';
import { DataTableFacetedFilter } from '@/components/common/data-table/data-table-faceted-filters';
import { QuoteSheetAssociateProduct } from './quote-sheet-associate-product';
import { QuoteSheetUnitPrice } from './quote-sheet-unit-price';
import { useUpdateQuoteSheetLineItems } from '@/api/procurement/useUpdateQuoteSheetLineItem';
import { useConfirmQuoteSheetMatch } from '@/api/procurement/useConfirmQuoteSheetMatch';
import { useRejectQuoteSheetMatch } from '@/api/procurement/useRejectQuoteSheetMatch';
import { useQueryClient } from '@tanstack/react-query';
import {
  getEffectiveProductVariant,
  getEffectiveSupplierProduct,
  hasAnySelection,
} from '../utils';
import {
  Product,
  ProductOutOfStock,
  Unknown,
} from '@/components/common/semantic-icons';
import { useReingestQuoteSheet } from '@/api/procurement/useReingestQuoteSheet';
import { QuoteSheetDTO } from '@/api/procurement/quote-sheets-types';
import { useMemo } from 'react';

interface LineItemsProps {
  items: QuoteSheetLineItemDTO[];
  quoteSheet: QuoteSheetDTO;
}

const lineItemsTableIconSizeCn = 'h-4 w-4';

const getStockIcon = (stock: QuoteSheetLineItemAvailability) => {
  switch (stock) {
    case 'AVAILABLE':
      return (
        <Product className={cn(lineItemsTableIconSizeCn, 'text-green-500')} />
      );
    case 'UNKNOWN':
      return (
        <Unknown className={cn(lineItemsTableIconSizeCn, 'text-gray-500')} />
      );
    case 'OUT_OF_STOCK':
      return (
        <ProductOutOfStock
          className={cn(lineItemsTableIconSizeCn, 'text-red-500')}
        />
      );
  }
};

enum QuoteSheetLineItemMatchStatus {
  UNMATCHED = 'UNMATCHED',
  BOT_SUGGESTED = 'BOT_SUGGESTED',
  BOT_CONFIRMED = 'BOT_CONFIRMED',
  BOT_CONFIRMED_PARTIAL = 'BOT_CONFIRMED_PARTIAL',
  SYSTEM_MATCHED = 'SYSTEM_MATCHED',
  USER_SELECTED = 'USER_SELECTED',
}

const MatchStatusToLabel: Record<QuoteSheetLineItemMatchStatus, string> = {
  [QuoteSheetLineItemMatchStatus.UNMATCHED]: 'Unmatched',
  [QuoteSheetLineItemMatchStatus.BOT_SUGGESTED]: 'Bot Suggested',
  [QuoteSheetLineItemMatchStatus.BOT_CONFIRMED]: 'Bot Confirmed',
  [QuoteSheetLineItemMatchStatus.BOT_CONFIRMED_PARTIAL]:
    'Bot Confirmed (Partial)',
  [QuoteSheetLineItemMatchStatus.SYSTEM_MATCHED]: 'System Matched',
  [QuoteSheetLineItemMatchStatus.USER_SELECTED]: 'User Selected',
};

const MatchStatusToIcon: Record<
  QuoteSheetLineItemMatchStatus,
  React.ReactNode
> = {
  [QuoteSheetLineItemMatchStatus.UNMATCHED]: (
    <Unknown className={cn(lineItemsTableIconSizeCn, 'text-gray-500')} />
  ),
  [QuoteSheetLineItemMatchStatus.BOT_SUGGESTED]: (
    <RobotIcon className={cn(lineItemsTableIconSizeCn, 'text-blue-500')} />
  ),
  [QuoteSheetLineItemMatchStatus.BOT_CONFIRMED]: (
    <RobotIcon className={cn(lineItemsTableIconSizeCn, 'text-green-500')} />
  ),
  [QuoteSheetLineItemMatchStatus.BOT_CONFIRMED_PARTIAL]: (
    <RobotIcon className={cn(lineItemsTableIconSizeCn, 'text-teal-500')} />
  ),
  [QuoteSheetLineItemMatchStatus.SYSTEM_MATCHED]: (
    <NotebookIcon className={cn(lineItemsTableIconSizeCn, 'text-green-500')} />
  ),
  [QuoteSheetLineItemMatchStatus.USER_SELECTED]: (
    <UserIcon className={cn(lineItemsTableIconSizeCn, 'text-green-500')} />
  ),
};

function useQuoteSheetLineItemMatchStatus(
  lineItem: QuoteSheetLineItemDTO,
): QuoteSheetLineItemMatchStatus {
  const selectedProductId =
    lineItem.selectedProductId || lineItem.selectedSupplierProduct?.productId;
  const selectedProductVariantId =
    lineItem.selectedSupplierProduct?.productVariantId;
  const selectedSupplierProductId = lineItem.selectedSupplierProductId;
  const suggestedProductId =
    lineItem.suggestedProductId || lineItem.suggestedSupplierProduct?.productId;
  const suggestedProductVariantId = lineItem.suggestedProductVariantId;
  const suggestedSupplierProductId = lineItem.suggestedSupplierProductId;

  const hasSuggestions = !!(
    suggestedProductId ||
    suggestedProductVariantId ||
    suggestedSupplierProductId
  );

  if ((selectedSupplierProductId || selectedProductId) && !hasSuggestions) {
    // FIXME: this is a hack to check if the line item was matched by the system or the user. Rerunning ingestion
    // and hitting a system match will show this as a user suggestion for now.
    return lineItem.createdAt === lineItem.updatedAt
      ? QuoteSheetLineItemMatchStatus.SYSTEM_MATCHED
      : QuoteSheetLineItemMatchStatus.USER_SELECTED;
  }

  // Most specific - variant or supplier product match
  if (
    (suggestedSupplierProductId &&
      selectedSupplierProductId === suggestedSupplierProductId) ||
    (suggestedProductVariantId &&
      selectedProductVariantId === suggestedProductVariantId)
  ) {
    return QuoteSheetLineItemMatchStatus.BOT_CONFIRMED;
  }

  // Partial match - product matched to suggested product
  if (
    suggestedProductId &&
    (lineItem.selectedProductId === lineItem.suggestedProductId ||
      lineItem.selectedProductId ===
        lineItem.suggestedSupplierProduct?.productId ||
      lineItem.selectedSupplierProduct?.productId ===
        lineItem.suggestedProductId)
  ) {
    return QuoteSheetLineItemMatchStatus.BOT_CONFIRMED_PARTIAL;
  }

  if (hasSuggestions) {
    return QuoteSheetLineItemMatchStatus.BOT_SUGGESTED;
  }

  return QuoteSheetLineItemMatchStatus.UNMATCHED;
}

function QuoteSheetLineItemRowActions({
  lineItem,
}: {
  lineItem: QuoteSheetLineItemDTO;
}) {
  const { mutate: deleteItem } = useDeleteQuoteSheetLineItem({
    quoteSheetId: lineItem.quoteSheetId,
    lineItemId: lineItem.id,
  });

  return (
    <DataTableRowActions
      actions={[
        <DropdownMenuItem
          variant="destructive"
          key="delete"
          onClick={() => deleteItem({})}
        >
          Delete
        </DropdownMenuItem>,
      ]}
    />
  );
}

function QuoteSheetLineItemAvailabilityDropdown({
  lineItem,
}: {
  lineItem: QuoteSheetLineItemDTO;
}) {
  const { mutate: updateLineItem } = useUpdateQuoteSheetLineItems({
    quoteSheetId: lineItem.quoteSheetId,
  });

  const handleAvailabilityChange = (
    availability: QuoteSheetLineItemAvailability,
  ) => {
    updateLineItem({
      lineItems: [{ ...lineItem, availability }],
    });
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger>
            {getStockIcon(
              lineItem.availability ?? QuoteSheetLineItemAvailability.UNKNOWN,
            )}
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs capitalize">
            {`Availability: ${lineItem.availability
              ?.toLowerCase()
              .replaceAll('_', ' ')}`}
          </span>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent>
        {Object.values(QuoteSheetLineItemAvailability).map((availability) => (
          <DropdownMenuItem
            key={availability}
            className="flex gap-1 items-center"
            onClick={() => handleAvailabilityChange(availability)}
          >
            {getStockIcon(availability)}
            {availability.replace('_', ' ')}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SuggestionSourceCell({
  lineItem,
}: {
  lineItem: QuoteSheetLineItemDTO;
}) {
  const queryClient = useQueryClient();
  const confirmMatch = useConfirmQuoteSheetMatch();
  const rejectMatch = useRejectQuoteSheetMatch();

  const hasSelection = hasAnySelection(lineItem);
  const matchStatus = useQuoteSheetLineItemMatchStatus(lineItem);
  const matchLabel = MatchStatusToLabel[matchStatus];
  const matchIcon = MatchStatusToIcon[matchStatus];

  const handleConfirmMatch = async () => {
    await confirmMatch.mutateAsync({
      lineItemId: lineItem.id,
      productId: lineItem.suggestedProductId || undefined,
      productVariantId: lineItem.suggestedProductVariantId || undefined,
      supplierProductId: lineItem.suggestedSupplierProductId || undefined,
    });

    // Invalidate queries to refresh the data
    queryClient.invalidateQueries({
      queryKey: [
        `accounts-payable/quote-sheets/${lineItem.quoteSheetId}`,
        undefined,
      ],
    });
  };

  const handleRejectMatch = async () => {
    await rejectMatch.mutateAsync({
      lineItemId: lineItem.id,
    });

    // Invalidate queries to refresh the data
    queryClient.invalidateQueries({
      queryKey: [
        `accounts-payable/quote-sheets/${lineItem.quoteSheetId}`,
        undefined,
      ],
    });
  };

  if (!hasSelection) {
    return <span className="w-4 h-4 inline-block" />;
  }

  if (matchStatus !== QuoteSheetLineItemMatchStatus.BOT_SUGGESTED) {
    return (
      <Tooltip>
        <TooltipTrigger>{matchIcon}</TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">{matchLabel}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  // AI suggested but not yet selected - show popover with confirm option
  return (
    <Popover>
      <PopoverTrigger>{matchIcon}</PopoverTrigger>
      <PopoverContent className="w-72 p-3">
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <T context="Label indicating a match was suggested by the AI system">
              AI Suggested Match
            </T>
          </div>
          <div className="text-sm">
            <T context="Prompt asking the user what action to take on the AI suggested match">
              What would you like to do?
            </T>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleConfirmMatch}
              disabled={confirmMatch.isLoading || rejectMatch.isLoading}
              className="h-8 px-3 flex-1"
            >
              <ThumbsUpIcon className="h-3 w-3 mr-1" />
              {confirmMatch.isLoading ? (
                <T context="Button text while the confirm action is in progress">
                  Confirming...
                </T>
              ) : (
                <T context="Button to confirm the AI suggested product match">
                  Confirm
                </T>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRejectMatch}
              disabled={confirmMatch.isLoading || rejectMatch.isLoading}
              className="h-8 px-3 flex-1"
            >
              <ThumbsDownIcon className="h-3 w-3 mr-1" />
              {rejectMatch.isLoading ? (
                <T context="Button text while the reject action is in progress">
                  Rejecting...
                </T>
              ) : (
                <T context="Button to reject the AI suggested product match">
                  Reject
                </T>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function QuoteSheetLineItems({ items, quoteSheet }: LineItemsProps) {
  const columns: ColumnDef<QuoteSheetLineItemDTO>[] = useMemo(
    () => [
      {
        id: 'status',
        cell: ({ row }) => (
          <QuoteSheetAssociateProduct lineItem={row.original} />
        ),
        header: function MatchColumnHeader() {
          const queryClient = useQueryClient();
          const { mutate: reingest, isLoading } = useReingestQuoteSheet({
            onSuccess: (data) => {
              if (data.success) {
                queryClient.invalidateQueries({
                  queryKey: [
                    `accounts-payable/quote-sheets/${quoteSheet.id}`,
                    undefined,
                  ],
                });
              }
            },
          });

          return (
            <RobotRetryButton
              onClick={() => reingest({ quoteSheetId: quoteSheet.id })}
              isLoading={isLoading}
              tooltip="Retry Product Matching"
            />
          );
        },
      },
      {
        id: 'suggestion-source',
        header: () => (
          <T context="Column header for the source/origin of the product match suggestion">
            Source
          </T>
        ),
        cell: ({ row }) => <SuggestionSourceCell lineItem={row.original} />,
        size: 60,
      },
      {
        accessorKey: 'productVariant.sku',
        header: 'SKU ID',
        footer: (props) => props.column.id,
        cell: ({ row }) => {
          const effectiveVariant = getEffectiveProductVariant(row.original);
          const effectiveSupplierProduct = getEffectiveSupplierProduct(
            row.original,
          );

          return (
            <span className="font-mono text-sm">
              {effectiveSupplierProduct?.sku || effectiveVariant?.sku ? (
                <span className="font-mono">
                  {effectiveSupplierProduct?.sku || effectiveVariant?.sku}
                </span>
              ) : (
                <span className="text-gray-500">-</span>
              )}
            </span>
          );
        },
        size: 100,
      },
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => {
          const lineItem = row.original;
          const effectiveProduct =
            lineItem.selectedProduct || lineItem.suggestedProduct;
          const effectiveVariant = getEffectiveProductVariant(lineItem);

          // Primary text: Raw/extracted product name from quote sheet
          const extractedProductName = String(
            lineItem.rawData?.productName || '',
          ).trim();

          // Build secondary text: Suggested/selected product and variant names
          const matchedProductInfo = [];

          // Show variant info if available, otherwise show product info
          if (effectiveVariant) {
            if (effectiveVariant.originMetadata?.NAMEINVC) {
              matchedProductInfo.push(
                `Variant: ${effectiveVariant.originMetadata.NAMEINVC}`,
              );
            } else if (effectiveVariant.sku) {
              matchedProductInfo.push(`Variant: ${effectiveVariant.sku}`);
            }
          } else if (effectiveProduct) {
            matchedProductInfo.push(`Product: ${effectiveProduct.name}`);
          }

          return (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {extractedProductName || (
                    <T context="Fallback text when no product name could be extracted from the quote sheet">
                      No product name extracted
                    </T>
                  )}
                </span>
              </div>
              {matchedProductInfo.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {matchedProductInfo.map((info, index) => (
                    <div key={index}>{info}</div>
                  ))}
                </div>
              )}
            </div>
          );
        },
        size: 280,
      },
      {
        accessorKey: 'availability',
        header: 'Stock',
        cell: ({ row }) => (
          <QuoteSheetLineItemAvailabilityDropdown lineItem={row.original} />
        ),
        size: 120,
      },
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        cell: ({ row }) => <QuoteSheetUnitPrice lineItem={row.original} />,
        size: 120,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <QuoteSheetLineItemRowActions lineItem={row.original} />
        ),
        size: 60,
      },
    ],
    [quoteSheet],
  );
  const table = useDataTable({
    data: items,
    columns,
    options: {
      initialState: {
        pagination: { pageIndex: 0, pageSize: 200 }, // TODO backend pagination
      },
      meta: {
        quoteSheet,
      },
      globalFilterFn: (row, _columnId, filterValue) => {
        const searchTerm = filterValue.toLowerCase();
        const lineItem = row.original;

        return (
          // Search in raw product name (primary display)
          String(lineItem.rawData?.productName || '')
            .toLowerCase()
            .includes(searchTerm) ||
          // Search in suggested/selected product names
          (lineItem.suggestedProduct?.name || '')
            .toLowerCase()
            .includes(searchTerm) ||
          (lineItem.selectedProduct?.name || '')
            .toLowerCase()
            .includes(searchTerm) ||
          // Search in supplier product name
          (lineItem.suggestedSupplierProduct?.name || '')
            .toLowerCase()
            .includes(searchTerm) ||
          (lineItem.selectedSupplierProduct?.name || '')
            .toLowerCase()
            .includes(searchTerm) ||
          // Search in variant names
          String(
            lineItem.suggestedProductVariant?.originMetadata?.NAMEINVC || '',
          )
            .toLowerCase()
            .includes(searchTerm) ||
          (lineItem.suggestedProductVariant?.sku || '')
            .toLowerCase()
            .includes(searchTerm)
        );
      },
    },
  });

  return (
    <div className="space-y-2 flex flex-col pb-2 overflow-auto">
      <h2 className="text-lg font-medium">
        <T context="heading for the quote sheet line items table">Line Items</T>
      </h2>
      <DataTableToolbar
        table={table}
        search={{
          placeholder: 'Filter line items',
          onChange: (e) => {
            table.setGlobalFilter(e.target.value);
          },
          value: table.state.globalFilter ?? '',
        }}
        facetedFilters={[
          <DataTableFacetedFilter
            key="stock"
            column={table.getColumn('availability')}
            title="Stock"
            options={Object.values(QuoteSheetLineItemAvailability).map(
              (availability) => ({
                label: availability.replace('_', ' '),
                value: availability,
              }),
            )}
          />,
        ]}
      />
      <DataTable className="overflow-y-auto" table={table} />
    </div>
  );
}
